// Readable text from an API error. The axios instance rejects with the response
// BODY (src/utils/axios.ts), never with the HTTP status, so callers read the
// shape of the body instead:
//   class-validator     { error: 'ValidationExceptions', messages: ['landing.x.y: …'] }
//   landing rules       { error: 'BadRequestException', messages: ['a.b: …'], code: 'LandingInvalid' }
//   conflict / gate     { message, code: 'LandingConflict' | 'PublishBlocked', detail }
// Extracted from UserNewEditForm (which keeps its own copy) and extended with
// `code`, `detail` and the per-field split used by the landing editor.

export type ApiError = {
  error?: string;
  code?: string;
  message?: string | string[];
  messages?: unknown[];
  detail?: any;
};

const messageText = (m: any): string => {
  if (typeof m === 'string') return m;
  if (m?.constraints) return Object.values(m.constraints).join(', ');
  return m?.message || JSON.stringify(m);
};

/** One readable string for a snackbar or an Alert. */
export function apiErrorText(err: any, fallback = 'Something went wrong'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (Array.isArray(err.messages) && err.messages.length) {
    return err.messages.map(messageText).join('\n');
  }
  if (Array.isArray(err.message)) return err.message.join('\n');
  return String(err.message || fallback);
}

/** The body's machine-readable `code` ('' when absent). */
export function apiErrorCode(err: any): string {
  return (err && typeof err === 'object' && typeof err.code === 'string' && err.code) || '';
}

export type FieldError = { path: string; message: string };

const FIELD_MESSAGE = /^([\w.]+): ([\s\S]*)$/;

/**
 * Split `<path>: <message>` entries into field errors. Applies to landing-rule
 * 400s (`code: 'LandingInvalid'`), class-validator 400s
 * (`error: 'ValidationExceptions'`) and any `messages[]` entry shaped like a
 * path. `stripPrefix` removes a leading root such as 'landing.'. Messages that
 * are not path-shaped come back in `other`.
 */
export function mapFieldErrors(
  err: any,
  stripPrefix = ''
): { fields: FieldError[]; other: string[] } {
  const fields: FieldError[] = [];
  const other: string[] = [];
  const list: unknown[] = Array.isArray(err?.messages)
    ? err.messages
    : Array.isArray(err?.message)
    ? err.message
    : [];
  list.forEach((raw) => {
    const text = messageText(raw);
    const hit = FIELD_MESSAGE.exec(text);
    if (!hit) {
      other.push(text);
      return;
    }
    let path = hit[1];
    if (stripPrefix && path.indexOf(stripPrefix) === 0) path = path.slice(stripPrefix.length);
    fields.push({ path, message: hit[2] });
  });
  if (!list.length && err) other.push(apiErrorText(err));
  return { fields, other };
}
